import { ScreenManager } from "../../managers/ScreenManager";
import { IconButton } from "../../ui/IconButton";
import { ApplyScalingBias } from "../../utils/ApplyScalingBias";
import { RectSize } from "../../utils/RectSize";
import { LoaderFileKeys } from "../constant/AppConstants";
import { LayoutDataManager } from "../managers/LayoutDataManager";
import { AppLocalization, LanguageId } from "../localization/AppLocalization";
import { AppSettings } from "../managers/AppSettings";
import { RulesContentsScroll } from "./rules/RulesContentsScroll";
import { ScrollableContentScreenBase } from "./ScrollableContentScreenBase";
import { VariationId } from "./TableScreen";
import { ThemeDataManager } from "../managers/ThemeDataManager";
import { ButtonEvents } from "../../ui/ButtonBase";
import { GTMTracking } from "../managers/GTMTracking";


/**
 * SetData -> UpdateView process explained:
 * 
 * This component builds and updates its view from 
 * @see curVariationId
 * @see curLangId
 * 
 * We pass not the JSON data nodes into @see SetData, but the above indexes,
 * let this comp rebuild itself accordingly (if applicable)
 * (this comp knows AppLocalization, also JSON structure).
 * The actual variationId inside AppLocalization doesnt change.
 * 
 * NOTE: Change of VariationId requires Update
 */
export class RulesScreen
    extends ScrollableContentScreenBase {
    declare public contentsScrollPane: RulesContentsScroll;
    public btnClose: IconButton;
    protected onCloseClick: Function;
    // following 2 vars reflect current component state
    protected curVariationId: VariationId;
    protected curLanguageId: LanguageId;


    /**
     * NOTE: 
     * 
     * @param onCloseClick 
     * @param variationId 
     * @param languageId 
     */
    public override Init(onCloseClick?: Function): void {
        // > ApplyMixins (Showable) + SetTweenProps
        super.Init();

        this.onCloseClick = onCloseClick;
    }

    public override Build(): void {
        // build subclass objects required by base
        // custom ContentsScrollPane
        this.contentsScrollPane = new RulesContentsScroll(this.scene);
        this.contentsScrollPane.Init(
            true,
            LayoutDataManager.GetSharedData().general.scrollableContent.size.innerScale,
        );
        this.contentsScrollPane.Build();

        // build base
        // > bg
        // > (add) contentsScrollPane
        // > btnPrev
        // > btnNext
        // > title
        super.Build();

        // btnClose
        this.btnClose = new IconButton(this.scene);
        this.btnClose.InitBase({
            out: { texture: LoaderFileKeys.ui, frame: "btn-round-bg-out.png" }
        });
        this.btnClose.InitIcon({
            out: { texture: LoaderFileKeys.ui, frame: "icon-x-black.png" }
        });
        this.btnClose.PostInit(50);
        // this.btnClose.setData("baseScale", 1.0);
        this.add(this.btnClose);

        // > contentsScrollPane
        // > title (contentsScrollData->capitalizedTitle)
        // this.UpdateFromLocalizationChange();
    }

    protected OnShowStart(animated: boolean): void {
        GTMTracking.PLDTrackingMenu("rules_"+VariationId[this.curVariationId]);
        super.OnShowStart(animated);
    }

    /**
     * RulesScreen facts:
     * - number of (leaf) items differs for each variation (ObjectPool and smart rebuild)
     * - ..
     * 
     * NOTE: call this before Show for proper SetSize/UpdateView of children 
     *       @see ScrollableContentScreenBase::OnShowStart
     * 
     * @param variationId 
     * @param languageId 
     */
    public SetData(
        variationId: VariationId = undefined,
        languageId: LanguageId = undefined,
    ): void {
        // console.log(`SetData\n${VariationId[this.curVariationId]}->${VariationId[variationId]}\n${LanguageId[this.curLanguageId]}->${LanguageId[languageId]}`);

        let isDirty: boolean = false;
        if (variationId != undefined && this.curVariationId != variationId) {
            this.curVariationId = variationId;
            isDirty = true;
        }
        if (languageId != undefined && this.curLanguageId != languageId) {
            this.curLanguageId = languageId;
            isDirty = true;
        }

        // if langauge or variation changed
        if (isDirty) {
            // update view with new LOC texts, reposition.
            this.contentsScrollPane.SetData(
                AppLocalization.GetVariationData(this.curVariationId).rules,
                ThemeDataManager.GetVariationData(this.curVariationId).rules
            );
            // update scroll data
            // EDIT: update additionally after setting defaults (update title to index 0)
            // this.UpdateFromLocalizationChange();
            isDirty = false;
        }
    }

    protected override RegisterListeners(): void {
        // > btnPrev
        // > btnNext
        // > contentsScrollPane
        // > ScreenManagerEvents.RESIZE
        super.RegisterListeners();

        this.btnClose.on(ButtonEvents.CLICK, this.onCloseClick, this);
    }

    protected override UnregisterListeners(): void {
        super.UnregisterListeners();

        this.btnClose.off(ButtonEvents.CLICK, this.onCloseClick, this);
    }

    protected override OnResize(windowRect: RectSize): void {
        // console.trace("OnResize");

        // > title
        // > btnPrev
        // > btnNext
        // > contentsScrollPane
        super.OnResize(windowRect);

        // const screenCenterX: number = (0.5 * windowRect.width) | 0;
        // const scrollPaneWidth = Math.min(windowRect.width * 0.8, this.maxWidth);
        // const titleScale: number = ApplyScalingBias(scrollPaneWidth / this.maxWidth, 1.0);
        const btnCloseScale = ScreenManager.GetAssetScale(
            LayoutDataManager.GetSharedData().general.screen.closeButton.baseScale,
            LayoutDataManager.GetSharedData().general.screen.closeButton.scaleBias,
        );

        // const btnCloseBaseScale = this.btnClose.getData("baseScale");
        // this.btnClose.scale = btnCloseBaseScale * btnCloseScale;
        this.btnClose.setScale(btnCloseScale);
        this.btnClose.setPosition(windowRect.width * 1.0 - 90 * btnCloseScale, 90 * btnCloseScale);
    }
}